import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { User, Mail, Phone, Globe, Lock, LogOut, Save } from "lucide-react";
import CountrySelect from "../components/CountrySelect";

/**
 * Account / Personal Details page.
 * Separate from /profile/edit which is for football data.
 * This is where users edit: name, phone, country, password.
 */
export default function Account() {
  const navigate = useNavigate();
  const { user, refreshUser, logout } = useAuth();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState(null);
  const [country, setCountry] = useState(null);
  const [saving, setSaving] = useState(false);
  // password
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    if (user === false) { navigate("/login"); return; }
    if (user) {
      setName(user.name || "");
      setPhone(user.phone || "");
      if (user.country) {
        // Try to resolve code from country name
        (async () => {
          try {
            const { data } = await api.get("/countries");
            const found = (data.countries || []).find((c) => c.name === user.country);
            if (found) { setCountryCode(found.code); setCountry(found); }
          } catch {}
        })();
      }
    }
  }, [user, navigate]);

  const saveAccount = async () => {
    setSaving(true);
    try {
      let phoneToSend = phone.trim();
      if (phoneToSend && !phoneToSend.startsWith("+") && country?.dial) {
        phoneToSend = `${country.dial}${phoneToSend.replace(/^0+/, "")}`;
      }
      await api.put("/account", {
        name: name.trim(),
        phone: phoneToSend || "",
        country: country?.name || null,
        country_code: countryCode,
      });
      toast.success("Account updated");
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (!cur || !next) { toast.error("Fill both password fields"); return; }
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setChangingPw(true);
    try {
      await api.post("/account/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed");
      setCur(""); setNext("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setChangingPw(false);
    }
  };

  if (!user) return <div className="min-h-screen bg-[#0A0A0A]" />;

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-8 sm:py-12" data-testid="account-page">
      <div className="max-w-2xl mx-auto px-5 sm:px-8">
        <div className="mb-8">
          <p className="text-[10px] text-[#D4AF37] tracking-[0.3em] uppercase mb-2">My account</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter uppercase text-[#F5F5F5]">
            Account details
          </h1>
          <p className="text-sm text-[#888] mt-2">Personal information and login. Your football profile lives separately.</p>
        </div>

        {/* Personal */}
        <section className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-md p-5 sm:p-7 mb-5">
          <div className="flex items-center gap-2 mb-5">
            <User size={14} className="text-[#D4AF37]" />
            <h2 className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#D4AF37]">Personal</h2>
          </div>

          <div className="space-y-4">
            <Field label="Full name">
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input-clean" data-testid="account-name" />
            </Field>

            <Field label="Email" hint="Cannot be changed (contact support)">
              <input type="email" value={user.email} disabled className="input-clean opacity-60 cursor-not-allowed" data-testid="account-email" />
            </Field>

            <Field label="Country">
              <CountrySelect
                value={countryCode}
                onChange={(code, c) => { setCountryCode(code); setCountry(c); }}
                testId="account-country"
                placeholder="Select your country"
              />
            </Field>

            <Field label="Phone / WhatsApp" hint="Include country code (e.g. +234...)">
              <div className="flex">
                <div className="flex items-center justify-center bg-[#0A0A0A] border border-r-0 border-[#262626] px-3 text-sm text-[#888] min-w-[60px]">
                  {country?.dial || "+—"}
                </div>
                <input
                  type="tel"
                  value={phone.startsWith(country?.dial || "+") && country?.dial ? phone.slice(country.dial.length) : phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^0-9+\s-]/g, ""))}
                  className="flex-1 input-clean rounded-l-none"
                  placeholder="Phone number"
                  data-testid="account-phone"
                />
              </div>
            </Field>

            <button
              onClick={saveAccount}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A0A0A] font-black tracking-widest uppercase text-xs px-6 py-3 rounded-full hover:bg-[#E5C04A] active:scale-[0.98] transition-all disabled:opacity-50"
              data-testid="account-save-btn"
            >
              <Save size={13} /> {saving ? "SAVING..." : "SAVE CHANGES"}
            </button>
          </div>
        </section>

        {/* Security */}
        <section className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-md p-5 sm:p-7 mb-5">
          <div className="flex items-center gap-2 mb-5">
            <Lock size={14} className="text-[#D4AF37]" />
            <h2 className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#D4AF37]">Security</h2>
          </div>

          <div className="space-y-4">
            <Field label="Current password">
              <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="input-clean" autoComplete="current-password" data-testid="account-current-pw" />
            </Field>
            <Field label="New password" hint="Minimum 8 characters">
              <input type="password" value={next} onChange={(e) => setNext(e.target.value)} className="input-clean" autoComplete="new-password" data-testid="account-new-pw" />
            </Field>
            <button
              onClick={changePassword}
              disabled={changingPw || !cur || !next}
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-[#262626] text-[#E5E5E5] uppercase tracking-wider text-[11px] px-5 py-2.5 rounded-full hover:border-[#D4AF37]/40 transition-colors disabled:opacity-50"
              data-testid="account-change-pw-btn"
            >
              {changingPw ? "Changing..." : "Change password"}
            </button>
          </div>
        </section>

        {/* Sign out */}
        <section className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-md p-5 sm:p-7 flex items-center justify-between gap-3">
          <div>
            <p className="text-sm text-[#E5E5E5] font-semibold">Signed in as</p>
            <p className="text-xs text-[#888]">{user.email}</p>
          </div>
          <button
            onClick={() => { if (logout) logout(); }}
            className="inline-flex items-center gap-2 text-[11px] text-[#888] hover:text-[#D4AF37] uppercase tracking-wider transition-colors"
            data-testid="account-logout-btn"
          >
            <LogOut size={12} /> Sign out
          </button>
        </section>

        <style>{`
          .input-clean { width: 100%; background: #0A0A0A; border: 1px solid #262626; color: #F5F5F5; font-size: 14px; padding: 12px 14px; border-radius: 2px; outline: none; transition: border-color 150ms ease; }
          .input-clean:focus { border-color: rgba(212,175,55,0.5); }
        `}</style>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-[10px] font-bold text-[#666] uppercase tracking-[0.2em] block mb-2">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-[#555] mt-1.5">{hint}</p>}
    </div>
  );
}
